// File: TimeSlot.tsx
import { useEffect, useState, useMemo } from "react";
import { useDrop } from 'react-dnd';
import styles from './TimeSlot.module.css'
import StudentOverlayPortal from "./StudentOverlayPortal";
import type { StudentDragItem, StudentOverlayData } from "./StudentOverlay";
import { formatSlotSpan } from "./utils/timeSlots";

export interface TimeSlotProps {
  day: string | number;
  time: string;
  onDrop: (item: StudentDragItem, day: string | number, time: string) => void;
  onDelete: (elementId: string) => void;
  onLockToggle?: (memberId: string) => void;
  active?: unknown;
  highlightedStudent?: string | null;
  studentOverlays?: StudentOverlayData[] | null;
}

export default function TimeSlot({
  day,
  time,
  onDrop,
  onDelete,
  onLockToggle,
  active,
  highlightedStudent,
  studentOverlays,
   }: TimeSlotProps) {


  const [isStudentStartTime, setIsStudentStartTime] = useState(false)
  const [thisStudentOverlay, setThisStudentOverlay] = useState<StudentOverlayData | null>(null)
  const [thisStudentName, setThisStudentName] = useState('')

  const timeSlotIdString = useMemo(() => {
    return `${day}_${time}_timeSlot`;
  }, [day, time]);

  useEffect(() => {
    if (studentOverlays === null || studentOverlays === undefined) {
      setIsStudentStartTime(false);
      setThisStudentOverlay(null);
      setThisStudentName('');
      return;
    }

    const newIsStudentStartTime = studentOverlays.length > 0;
    setIsStudentStartTime(newIsStudentStartTime);

    if (newIsStudentStartTime) {
      const foundOverlay = studentOverlays[0];
      if (foundOverlay) {
        setThisStudentOverlay(foundOverlay);
        setThisStudentName(foundOverlay.studentName);
      } else {
        console.warn(`[TimeSlot ${timeSlotIdString}] Expected overlay but found none in prop.`);
        setThisStudentOverlay(null);
        setThisStudentName('');
        setIsStudentStartTime(false);
      }
    } else {
      setThisStudentOverlay(null);
      setThisStudentName('');
    }

  }, [studentOverlays, timeSlotIdString]);

  // Ablage-Vorschau: Nur die Zelle unter dem Mauszeiger bekommt das gezogene
  // Item, alle anderen null. Beim Überfahren rendern so nur die alte und die
  // neue Zelle neu, nicht das ganze Raster.
  const [{ hoverItem }, drop] = useDrop<StudentDragItem, void, { hoverItem: StudentDragItem | null }>(() => ({
      accept: 'STUDENT',
      drop: (item) => {
        console.log("[TimeSlot drop] Item dropped:", item, "on day:", day, "at time:", time);
        onDrop(item, day, time);
      },
      collect: (monitor) => ({ hoverItem: monitor.isOver() ? monitor.getItem() : null }),
  }));

  return (
    <div
      id={timeSlotIdString}
      ref={(node) => { drop(node); }}
      className={styles.timeSlot}
      data-day={day}
      data-active={active}
    >
      {hoverItem && (
        <div
          className={styles.dropPreview}
          style={{ height: `calc(${hoverItem.duration * 100}% - 2px)` }}
          data-testid="drop-preview"
          aria-hidden="true"
        >
          <span className={styles.dropPreviewLabel}>{formatSlotSpan(time, hoverItem.duration)}</span>
        </div>
      )}
      { isStudentStartTime && thisStudentOverlay
      ? (<StudentOverlayPortal
          key={thisStudentOverlay.memberId || thisStudentOverlay.studentId || thisStudentOverlay.studentName}
          thisStudent={thisStudentOverlay}
          containerId={timeSlotIdString}
          onDelete={onDelete}
          onLockToggle={onLockToggle}
          isHighlighted={highlightedStudent === thisStudentName}
          />)
      : null
     }
    </div>
  );
};
